import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { StructureDetailShellProps } from './StructureDetailShell.props';

/**
 * The page frame shared by both structure detail variants — breadcrumb, sticky
 * summary bar, then the panel stack.
 *
 * Both the internal and the client/customer variant render the same chrome, so
 * it lives here rather than being copied into each: they drifted apart the last
 * time the page was touched, and one of them is what a client actually sees.
 */
export const StructureDetailShell: React.FC<StructureDetailShellProps> = ({
  breadcrumbLabel,
  summaryBar,
  tabStrip,
  children,
  embedded = false,
}) => {
  const { pathname } = useLocation();
  // Keep the user in the section they came from — the structure list is mounted
  // at both paths, and jumping between them loses the surrounding navigation.
  const listHref = pathname.startsWith('/configure')
    ? '/configure/structure/search'
    : '/structure/search';

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: embedded ? undefined : '100vh',
        pb: 2,
      }}
    >
      {!embedded && (
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            fontSize: '13px',
            color: 'text.secondary',
            display: 'flex',
            gap: 0.75,
            '@media print': { display: 'none' },
          }}
        >
          <MuiLink
            component={RouterLink}
            to={listHref}
            underline="none"
            sx={{ color: 'v4.accent' }}
          >
            Structures
          </MuiLink>
          <span>/</span>
          <Box
            component="span"
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {breadcrumbLabel}
          </Box>
        </Box>
      )}

      <Box
        sx={(theme) => ({
          position: embedded ? 'static' : 'sticky',
          top: 0,
          zIndex: 50,
          bgcolor: 'background.default',
          px: embedded ? 0 : 2.5,
          py: 1,
          boxShadow: embedded ? 'none' : theme.shadows[2],
        })}
      >
        {summaryBar}
      </Box>

      {tabStrip}

      <Box
        sx={{
          px: embedded ? 0 : 2.5,
          pt: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
